import React, { useMemo, useState, useEffect } from 'react';
import { IAbstractRecord } from '@models';
import Stepper from './stepper/Stepper';
import { getLineCategory, LineCategory } from '@utils/helper-utils';
import MotorAmendment from '../line-amendment/MotorAmendment';
import MedicalAmendment from '../line-amendment/MedicalAmendment';
import TravelAmendment from '../line-amendment/TravelAmendment';
import ExpatAmendment from '../line-amendment/ExpatAmendment';
import GenericAmendment from '../line-amendment/GenericAmendment';
import { IAmendmentGeneralData, ILineAmendmentProps } from '../line-amendment';
import { makeStyles } from 'tss-react/mui';
import { IAmendmentPageState, IPricingOptions } from '..';
import { getAmendmentInitialState } from '../content';
import { useParams } from 'react-router-dom';
import AmendmentOfferWidget from '../components/AmendmentOfferWidget';
import { useLazyQuery } from '@apollo/client';
import {
  getCancellationPricingOptionsQuery,
  getPricingOptionsQuery,
} from '../queries';
import { useAppSelector } from '@redux/hooks';
import CancellationAmendmentOfferWidget from '../components/CancellationAmendmentOfferWidget';
import CancellationDetailsWidget from '../CancellationDetailsWidget';

interface IMainColumn {
  data: IAbstractRecord;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  mainContainer: {
    margin: '20px 0',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '1em',
    alignItems: 'start',
  },
  widgetsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.8em',
    '& .amendment-offer-widget': {
      padding: 0,
    },
  },
}));

const LineCategoryComponents: Record<
  LineCategory,
  React.FC<ILineAmendmentProps>
> = {
  Motor: MotorAmendment,
  Medical: MedicalAmendment,
  Travel: TravelAmendment,
  Expat: ExpatAmendment,
  Generic: GenericAmendment,
};

const MainColumn: React.FC<IMainColumn> = ({ data, onSuccess }) => {
  // 1) Set up lazy queries
  const [getPricingOptions] = useLazyQuery(getPricingOptionsQuery);
  const [getCancellationPricingOptions] = useLazyQuery(
    getCancellationPricingOptionsQuery
  );

  const { id: currentAmendmentId } = useParams<{ id: string }>();
  const { classes } = useStyles();

  const loggedInUser = useAppSelector((state) => state.user);

  const amendmentData =
    data?.Production?.queries?.getCancellationAmendmentDetails?.[0] ||
    data?.Production?.queries?.getAmendmentDetails?.[0];

  const lineCategory = getLineCategory(amendmentData?.line_ExternalCode);
  const LineComponents: React.FC<ILineAmendmentProps> =
    LineCategoryComponents[lineCategory];

  const [pricingOption, setPricingOption] = useState<Record<
    string,
    Record<string, string>
  > | null>(null);

  const [cancellationPricingOption, setCancellationPricingOption] =
    useState<Record<'Current' | 'Variance', IPricingOptions> | null>(null);

  const [pageState, setPageState] = useState<IAmendmentPageState>(
    getAmendmentInitialState(lineCategory)
  );

  const generalData: IAmendmentGeneralData = useMemo(() => {
    if (!amendmentData)
      return {
        amendment: { id: '', status: '', type: '' },
        line: { id: '', category: LineCategory.Generic, externalCode: '' },
        proposal: { id: '', planToPolicyRate: 0 },
        policy: {
          id: '',
          type: '',
          effectiveDate: '',
          expiryDate: '',
          version: '',
          versionId: '',
          currentVersion: '',
          clauseText: '',
        },
        plan: { id: '', name: '', isClauseEditable: false },
        loggedInUser: { allowPlanCoverEdits: false, allowClauseEdits: false },
        primaryBP: { id: '' },
        businessUser: { editableLines: [] },
        currency: { Code: '', Title: '', Symbol: '' },
      };

    const policyId = amendmentData?.policy_Id;
    const newVersionId =
      amendmentData?.production_Amendment_NewPolicyVersionID?.id || null;
    const newVersionNumber =
      amendmentData?.production_Amendment_NewPolicyVersionID?.views
        ?.Production_all?.properties?.Version || null;
    const currentVersionNumber =
      amendmentData?.production_Amendment_CurrentPolicyVersionID?.views
        ?.Production_all?.properties?.Version || null;

    if (policyId) {
      if (
        amendmentData.production_Amendment_AmendmentType ===
        'POLICY_CANCELLATION'
      ) {
        getCancellationPricingOptions({
          variables: {
            selectedAmendmentID: currentAmendmentId,
          },
        }).then((result) => {
          const cancellationData = result?.data?.Production?.queries || [];
          const finalData =
            cancellationData.getCancellationAmendmentDetails[0] || {};

          setCancellationPricingOption({
            Current: {
              BasicPremium: finalData.pricingOption_BasicPremium,
              NetPremium: finalData.pricingOption_NetPremium,
              PolicyCostAmount: finalData.pricingOption_PolicyCost,
              ChargesAmount: finalData.pricingOption_ChargesAmount,
              TPAFeesTotalAmount: finalData.pricingOption_TPAFeesAmount,
              GrossPremium: finalData.pricingOption_GrossPremium,
              FixedStampAmount: finalData.pricingOption_FixedStampAmount,
              ProportionalStampAmount:
                finalData.pricingOption_ProportionalStampAmount,
              MunicipalityTaxAmount:
                finalData.pricingOption_MunicipalityTaxAmount,
              TotalPremium: finalData.pricingOption_TotalPremium,
              currencySymbol: '',
            },
            Variance: {
              BasicPremium: finalData.amendmentPOVariance_AmendmentBasicPremium,
              NetPremium: finalData.amendmentPOVariance_AmendmentNetPremium,
              PolicyCostAmount: finalData.amendmentPOVariance_AmendmentCost,
              ChargesAmount:
                finalData.amendmentPOVariance_AmendmentChargesAmount,
              TPAFeesTotalAmount:
                finalData.amendmentPOVariance_AmendmentTPAFeesAmount,
              GrossPremium: finalData.amendmentPOVariance_AmendmentGrossPremium,
              FixedStampAmount:
                finalData.amendmentPOVariance_AmendmentFixedStampAmount,
              ProportionalStampAmount:
                finalData.amendmentPOVariance_AmendmentProportionalStampAmount,
              MunicipalityTaxAmount:
                finalData.amendmentPOVariance_AmendmentMunicipalityTaxAmount,
              TotalPremium: finalData.amendmentPOVariance_AmendmentTotalPremium,
              currencySymbol: '',
            },
          });
        });
      } else {
        if (amendmentData.production_Amendment_AmendmentStatus !== 'NEW') {
          getPricingOptions({
            variables: {
              selectedPolicyID: policyId,
              selectedAmendmentID: currentAmendmentId,
              newVersion: newVersionNumber,
              currentVersion: currentVersionNumber,
            },
          }).then((result) => {
            const { data } = result;
            const {
              current: currentQuery,
              new: newQuery,
              variance: varianceQuery,
            } = data.Production.queries;

            const current = currentQuery?.[0] || {};
            const variance = varianceQuery?.[0] || {};
            const newPricingOption = newQuery?.[0] || {};

            setPricingOption(
              amendmentData.production_Amendment_AmendmentType !=
                'POLICY_CORRECTION'
                ? {
                    Current: {
                      BasicPremium:
                        current.production_PricingOption_BasicPremium,
                      NetPremium: current.production_PricingOption_NetPremium,
                      PolicyCostAmount:
                        current.production_PricingOption_PolicyCostAmount,
                      ChargesAmount:
                        current.production_PricingOption_ChargesAmount,
                      TPAFeesTotalAmount:
                        current.production_PricingOption_TPAFeesTotalAmount,
                      GrossPremium:
                        current.production_PricingOption_GrossPremium,
                      FixedStampAmount:
                        current.production_PricingOption_FixedStampAmount,
                      ProportionalStampAmount:
                        current.production_PricingOption_ProportionalStampAmount,
                      MunicipalityTaxAmount:
                        current.production_PricingOption_MunicipalityTaxAmount,
                      TotalPremium:
                        current.production_PricingOption_TotalPremium,
                      currencySymbol:
                        current.production_PricingOption_PricingOptionCurrency
                          ?.Symbol || '',
                    },
                    Variance: {
                      BasicPremium:
                        variance.amendmentPricingOptionVariance_AmendmentBasicPremium,
                      NetPremium:
                        variance.amendmentPricingOptionVariance_AmendmentNetPremium,
                      PolicyCostAmount:
                        amendmentData.amendmentPricingOptionVariance_AmendmentCostAmount,
                      ChargesAmount:
                        variance.amendmentPricingOptionVariance_AmendmentChargesAmount,
                      TPAFeesTotalAmount:
                        amendmentData.amendmentPricingOptionVariance_AmendmentTPAFeesAmount,
                      GrossPremium:
                        variance.amendmentPricingOptionVariance_AmendmentGrossPremium,
                      FixedStampAmount:
                        variance.amendmentPricingOptionVariance_AmendmentFixedStampAmount,
                      ProportionalStampAmount:
                        variance.amendmentPricingOptionVariance_AmendmentProportionalStampAmount,
                      MunicipalityTaxAmount:
                        variance.amendmentPricingOptionVariance_AmendmentMunicipalityTaxAmount,
                      TotalPremium:
                        variance.amendmentPricingOptionVariance_AmendmentTotalPremium,
                      currencySymbol:
                        variance
                          .amendmentPricingOptionVariance_PricingOptionCurrency
                          ?.Symbol || '',
                    },
                    New: {
                      BasicPremium:
                        newPricingOption.production_PricingOption_BasicPremium,
                      NetPremium:
                        newPricingOption.production_PricingOption_NetPremium,
                      PolicyCostAmount:
                        newPricingOption.production_PricingOption_PolicyCostAmount,
                      ChargesAmount:
                        newPricingOption.production_PricingOption_ChargesAmount,
                      TPAFeesTotalAmount:
                        newPricingOption.production_PricingOption_TPAFeesTotalAmount,
                      GrossPremium:
                        newPricingOption.production_PricingOption_GrossPremium,
                      FixedStampAmount:
                        newPricingOption.production_PricingOption_FixedStampAmount,
                      ProportionalStampAmount:
                        newPricingOption.production_PricingOption_ProportionalStampAmount,
                      MunicipalityTaxAmount:
                        newPricingOption.production_PricingOption_MunicipalityTaxAmount,
                      TotalPremium:
                        newPricingOption.production_PricingOption_TotalPremium,
                      currencySymbol:
                        newPricingOption
                          .production_PricingOption_PricingOptionCurrency
                          ?.Symbol || '',
                    },
                  }
                : {
                    New: {
                      BasicPremium:
                        newPricingOption.production_PricingOption_BasicPremium,
                      NetPremium:
                        newPricingOption.production_PricingOption_NetPremium,
                      PolicyCostAmount:
                        newPricingOption.production_PricingOption_PolicyCostAmount,
                      ChargesAmount:
                        newPricingOption.production_PricingOption_ChargesAmount,
                      TPAFeesTotalAmount:
                        newPricingOption.production_PricingOption_TPAFeesTotalAmount,
                      GrossPremium:
                        newPricingOption.production_PricingOption_GrossPremium,
                      FixedStampAmount:
                        newPricingOption.production_PricingOption_FixedStampAmount,
                      ProportionalStampAmount:
                        newPricingOption.production_PricingOption_ProportionalStampAmount,
                      MunicipalityTaxAmount:
                        newPricingOption.production_PricingOption_MunicipalityTaxAmount,
                      TotalPremium:
                        newPricingOption.production_PricingOption_TotalPremium,
                      currencySymbol:
                        newPricingOption
                          .production_PricingOption_PricingOptionCurrency
                          ?.Symbol || '',
                    },
                  }
            );
          });
        }
      }
    }

    const lines =
      amendmentData.policy_BusinessUserID?.views?.SalesforceManagement_all
        ?.properties?.EditableLineChargesIDs || [];
    const plan =
      amendmentData.policy_PlanID?.views?.PlanConfigManagement_all?.properties;

    return {
      amendment: {
        id: currentAmendmentId,
        status: amendmentData.production_Amendment_AmendmentStatus,
        type: amendmentData.production_Amendment_AmendmentType,
      },
      line: {
        id: amendmentData.line_Id,
        category: lineCategory,
        externalCode: amendmentData.line_ExternalCode,
      },
      proposal: {
        id: amendmentData.policy_ProposalID?.id,
        planToPolicyRate:
          amendmentData.policy_ProposalID?.queries?.production?.pricingOption
            ?.pricingOption_PlanToPolicyRate,
      },
      policy: {
        id: policyId,
        type: amendmentData.policy_Type,
        effectiveDate: amendmentData.policy_PolicyEffectiveDate,
        expiryDate: amendmentData.policy_PolicyExpiryDate,
        version: newVersionNumber,
        versionId: newVersionId,
        currentVersion: currentVersionNumber,
        clauseText: amendmentData.policy_ClauseText,
      },
      plan: {
        id: amendmentData.plan_Id,
        name: amendmentData.plan_Name,
        isClauseEditable: plan?.ClauseEditable,
      },
      loggedInUser: {
        allowPlanCoverEdits: loggedInUser.info.businessUser_AllowPlanCoverEdits,
        allowClauseEdits: loggedInUser.info.businessUser_AllowClauseEdits,
      },
      primaryBP: {
        id: amendmentData.policy_PrimaryBPID?.id,
      },
      businessUser: {
        editableLines: lines.map((line: IAbstractRecord) => {
          return line.views.PlanConfigManagement_all.properties.ExternalCode;
        }),
      },
      currency: amendmentData?.policy_PolicyCurrency,
    };
  }, [
    currentAmendmentId,
    lineCategory,
    amendmentData,
    loggedInUser,
    getPricingOptions,
    getCancellationPricingOptions,
  ]);

  return (
    <>
      <Stepper
        generalData={generalData}
        values={pageState.values}
        onSuccess={onSuccess}
      />
      <section className={classes.mainContainer}>
        <div className={classes.widgetsContainer}>
          {generalData.amendment.type === 'POLICY_CANCELLATION' ? (
            <CancellationDetailsWidget
              generalData={generalData}
              data={data}
              pageState={pageState}
            />
          ) : (
            <LineComponents
              generalData={generalData}
              data={data}
              pageState={pageState}
              handleStateChange={setPageState}
            />
          )}
        </div>

        {generalData.amendment.status !== 'NEW' &&
          generalData.amendment.type !== 'POLICY_CANCELLATION' &&
          pricingOption && (
            <div className={classes.widgetsContainer}>
              <AmendmentOfferWidget
                data={{ PricingOption: pricingOption }}
                type={generalData.amendment.type}
              />
            </div>
          )}

        {generalData.amendment.type === 'POLICY_CANCELLATION' && (
          <div className={classes.widgetsContainer}>
            <CancellationAmendmentOfferWidget
              data={{ PricingOption: cancellationPricingOption }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default MainColumn;
